import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  serverTimestamp 
} from 'firebase/firestore';
import HierarchicalServices from './HierarchicalServices';
import TasksPublicServices from './TasksPublicServices';
import TasksAIServices from './TasksAIServices';

/**
 * Generic Firebase service for CRUD operations on collections
 * Example usage:
 * const result = await getItems("books", {
 *   sort: "price", 
 *   order: "asc",
 *   page: 1,
 *   perPage: 10,
 *   filter: { authorId: "123", genre: "fiction" }
 * });
 */
const FirebaseService = {
  getItems: async (collectionName, obj = {}) => {
    return await HierarchicalServices.getItems(collectionName, obj);
  },


  getItem: async (collectionName, obj) => {
    return await HierarchicalServices.getItem(collectionName, obj);
  },

  addItem: async (collectionName, obj) => {
    
    return await HierarchicalServices.addItem(collectionName, obj);
  },


  updateItem: async (collectionName, obj = {}) => {
    return await HierarchicalServices.updateItem(collectionName, obj);
  },

  deleteItem: async (collectionName, obj) => {
    return await HierarchicalServices.deleteItem(collectionName, obj);
  },

  subscribeToItems: (collectionName, obj, callback) => {
    return HierarchicalServices.subscribeToItems(collectionName, obj, callback);
  },

  subscribeToItem: (collectionName, obj, callback) => {
    return HierarchicalServices.subscribeToItem(collectionName, obj, callback);
  },

  searchItems: async (collectionName, obj = {}) => {
    const searchValue = Object.values(obj).find(
      (value) => value !== undefined && value !== null && value !== ''
    );

    const result = await HierarchicalServices.getItems(collectionName, {
      search: searchValue || false,
      getAll: true,
      filter: obj,
    });

    if (result?.error) {
      return result;
    }

    return result?.items || result;
  },

  // Tasks Public API
  getPublicTasks: (obj) => TasksPublicServices.getPublicItems(obj),
  getPublicTask: (obj) => TasksPublicServices.getPublicItem(obj),
  getPublicTaskStats: () => TasksPublicServices.getPublicStats(),
  searchPublicTasks: (obj) => TasksPublicServices.searchPublicItems(obj),
  getPublicTaskTags: () => TasksPublicServices.getPublicTags(),

  // Tasks AI API
  executeTaskAction: (obj, user) => TasksAIServices.executeAction(obj, user),
  completeTask: (taskId, user) => TasksAIServices.completeTask(taskId, user),
  reopenTask: (taskId, user) => TasksAIServices.reopenTask(taskId, user),
  setTaskStatus: (taskId, status, user) => TasksAIServices.setStatus(taskId, status, user),
  setTaskPriority: (taskId, priority, user) => TasksAIServices.setPriority(taskId, priority, user),
  setTaskDueDate: (taskId, dueDate, user) => TasksAIServices.setDueDate(taskId, dueDate, user),
  setTaskTitle: (taskId, title, user) => TasksAIServices.setTitle(taskId, title, user),
  setTaskDescription: (taskId, description, user) => TasksAIServices.setDescription(taskId, description, user),
  updateTaskNotes: (taskId, notes, user) => TasksAIServices.updateNotes(taskId, notes, user),
  appendTaskNotes: (taskId, notes, user) => TasksAIServices.appendNotes(taskId, notes, user),
  setTaskVisibility: (taskId, visibility, user) => TasksAIServices.setVisibility(taskId, visibility, user),
  assignTask: (taskId, assignedTo, user) => TasksAIServices.assignTask(taskId, assignedTo, user),
  unassignTask: (taskId, user) => TasksAIServices.unassignTask(taskId, user),
  addTaskTags: (taskId, tags, user) => TasksAIServices.addTags(taskId, tags, user),
  removeTaskTags: (taskId, tags, user) => TasksAIServices.removeTags(taskId, tags, user),
  setTaskTags: (taskId, tags, user) => TasksAIServices.setTags(taskId, tags, user),
  addTaskChecklistItem: (taskId, text, user) => TasksAIServices.addChecklistItem(taskId, text, user),
  updateTaskChecklistItem: (taskId, itemId, patch, user) => TasksAIServices.updateChecklistItem(taskId, itemId, patch, user),
  toggleTaskChecklistItem: (taskId, itemId, completed, user) => TasksAIServices.toggleChecklistItem(taskId, itemId, completed, user),
  removeTaskChecklistItem: (taskId, itemId, user) => TasksAIServices.removeChecklistItem(taskId, itemId, user),
  completeAllTaskChecklistItems: (taskId, user) => TasksAIServices.completeAllChecklistItems(taskId, user),
  addTaskChecklistChildItem: (taskId, parentItemId, text, user) => TasksAIServices.addChecklistChildItem(taskId, parentItemId, text, user),
  updateTaskChecklistChildItem: (taskId, parentItemId, childId, patch, user) => TasksAIServices.updateChecklistChildItem(taskId, parentItemId, childId, patch, user),
  toggleTaskChecklistChildItem: (taskId, parentItemId, childId, completed, user) => TasksAIServices.toggleChecklistChildItem(taskId, parentItemId, childId, completed, user),
  removeTaskChecklistChildItem: (taskId, parentItemId, childId, user) => TasksAIServices.removeChecklistChildItem(taskId, parentItemId, childId, user),
  duplicateTask: (taskId, user) => TasksAIServices.duplicateTask(taskId, user),
  bulkUpdateTasks: (taskIds, updates, user) => TasksAIServices.bulkUpdate(taskIds, updates, user),
  bulkCompleteTasks: (taskIds, user) => TasksAIServices.bulkComplete(taskIds, user),
  bulkDeleteTasks: (taskIds, payload, user) => TasksAIServices.bulkDelete(taskIds, payload, user),
  listTasksAI: (payload, user) => TasksAIServices.listTasks(payload, user),
  getTaskAI: (taskId, user) => TasksAIServices.getTask(taskId, user),
  searchTasksAI: (payload, user) => TasksAIServices.searchTasks(payload, user),
  getTaskStats: (user) => TasksAIServices.getStats(user),
  getTaskTags: (user) => TasksAIServices.getTags(user),
  getOverdueTasks: (user) => TasksAIServices.getOverdueTasks(user),
};

export default FirebaseService; 